import { useEffect, useRef, useState } from 'preact/hooks';
import useFixedHeaderHeight from './useFixedHeaderHeight.tsx';

export const useIntersection = <T extends HTMLElement>(
  options?: IntersectionObserverInit & { onFirstInteraction?: () => void }
) => {
  const [isVisible, setState] = useState(false);
  const fired = useRef(false);
  const fixedHeaderHeight = useFixedHeaderHeight();
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const { current } = elementRef;
    const defaultOptions = fixedHeaderHeight
      ? {
          rootMargin: `-${fixedHeaderHeight}px 0px 0px 0px`
        }
      : {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        // A ref is preferable over state for tracking onFirstInteraction
        // because the event might fire multiple times before the state is updated.
        if (!fired.current && entry.isIntersecting) {
          fired.current = true;
          options?.onFirstInteraction?.();
        }

        // The "isVisible" state should be updated after the fired ref
        setState(entry.isIntersecting);
      },
      {
        ...defaultOptions,
        ...options
      }
    );
    if (current) {
      observer?.observe(current);
    }
    return () => current && observer.unobserve(current);
  }, [fixedHeaderHeight]);

  return { elementRef, isVisible, hasFired: fired.current };
};
